import React, { useState, useRef, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { nodeUrl } from '@/lib/taxonomyUrl';

const INITIAL_CATS = 8;
const GAP = 6; // px — matches gap-x-1.5

// One category rendered on EXACTLY one horizontal row:
//   Category — Sub · Sub · Sub … +N more
// The number of visible subcategories is computed from the ACTUAL available
// width (responsive via ResizeObserver), not a fixed count. Subcategories never
// wrap to a second line — a hidden measurer sizes each item, and the visible
// row also uses nowrap+overflow-hidden as a hard guarantee. "+N more" links to
// the category page (clean canonical URL) where every subcategory is listed.
const OneLineCategoryRow = ({ cat }) => {
  const subs = cat.subcategories || cat.children || [];
  const wrapRef = useRef(null);
  const measureRef = useRef(null);
  const [visible, setVisible] = useState(subs.length);
  const [expanded, setExpanded] = useState(false);

  useLayoutEffect(() => {
    const compute = () => {
      const wrap = wrapRef.current;
      const meas = measureRef.current;
      if (!wrap || !meas) return;
      const cw = wrap.clientWidth - 8; // small safety buffer
      const kids = Array.from(meas.children);
      if (kids.length < 1) return;
      const nameW = kids[0].getBoundingClientRect().width;
      const moreW = kids[kids.length - 1].getBoundingClientRect().width;
      const subEls = kids.slice(1, 1 + subs.length);
      let used = nameW;
      let fit = 0;
      for (let i = 0; i < subEls.length; i++) {
        const w = subEls[i].getBoundingClientRect().width + GAP;
        const willHaveHidden = i < subEls.length - 1; // more remain after this one
        const reserve = willHaveHidden ? moreW + GAP : 0;
        if (used + w + reserve <= cw) { used += w; fit += 1; } else break;
      }
      setVisible(fit);
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [subs]);

  const hidden = subs.length - visible;

  return (
    <div ref={wrapRef} className='relative w-full'>
      {/* hidden measurer — renders ALL items to size them accurately */}
      <div
        ref={measureRef}
        aria-hidden='true'
        className='invisible pointer-events-none absolute left-0 top-0 flex items-baseline gap-x-1.5 whitespace-nowrap text-sm'
      >
        <span className='font-semibold'>{cat.name}{subs.length > 0 ? ' —' : ''}</span>
        {subs.map((s, i) => (
          <span key={s.id}>{i > 0 ? '· ' : ''}{s.name}</span>
        ))}
        <span className='text-xs font-medium'>+{subs.length} more</span>
      </div>

      {/* visible row — collapsed: strictly one line (nowrap + clip); expanded:
          wraps to reveal every subcategory (one-line rule applies to collapsed
          state only). "+N more" / "Show less" toggle only — no navigation. */}
      <div className={`flex items-baseline gap-x-1.5 text-sm leading-relaxed ${expanded ? 'flex-wrap gap-y-1' : 'overflow-hidden whitespace-nowrap'}`}>
        <Link
          to={nodeUrl(cat)}
          className='shrink-0 font-semibold text-foreground transition-colors hover:text-primary'
          data-testid={`browse-cat-${cat.slug}`}
        >
          {cat.name}
        </Link>
        {subs.length > 0 && (expanded || visible > 0) && <span className='shrink-0 text-muted-foreground/40'>—</span>}
        {(expanded ? subs : subs.slice(0, visible)).map((s, i) => (
          <React.Fragment key={s.id}>
            {i > 0 && <span className='shrink-0 text-muted-foreground/40'>·</span>}
            <Link
              to={nodeUrl(s)}
              className='shrink-0 text-muted-foreground transition-colors hover:text-primary'
              data-testid={`browse-sub-${s.slug}`}
            >
              {s.name}
            </Link>
          </React.Fragment>
        ))}
        {!expanded && hidden > 0 && (
          <button
            type='button'
            onClick={() => setExpanded(true)}
            className='ml-1 shrink-0 text-xs font-medium text-primary hover:underline'
            data-testid={`browse-cat-more-${cat.slug}`}
          >
            +{hidden} more
          </button>
        )}
        {expanded && subs.length > 0 && (
          <button
            type='button'
            onClick={() => setExpanded(false)}
            className='ml-1 shrink-0 text-xs font-medium text-muted-foreground hover:underline'
            data-testid={`browse-cat-showless-${cat.slug}`}
          >
            Show less
          </button>
        )}
      </div>
    </div>
  );
};

// Compact, scalable Category → Subcategory navigation for the Type pages.
// One row per category; overflow categories (types with >8) collapse behind a
// "Show all N categories" toggle so it also scales to 20/50+ categories.
const TypeTaxonomyNav = ({ tree = [] }) => {
  const [showAllCats, setShowAllCats] = useState(false);
  if (!tree.length) return null;
  const cats = showAllCats ? tree : tree.slice(0, INITIAL_CATS);
  return (
    <div className='mb-6 rounded-xl border bg-card px-4 py-3 space-y-1' data-testid='browse-category-nav'>
      {cats.map((cat) => <OneLineCategoryRow key={cat.id} cat={cat} />)}
      {tree.length > INITIAL_CATS && (
        <button
          type='button'
          onClick={() => setShowAllCats((v) => !v)}
          className='pt-1 text-xs font-semibold text-primary hover:underline'
          data-testid='browse-show-all-cats'
        >
          {showAllCats ? 'Show fewer categories' : `Show all ${tree.length} categories`}
        </button>
      )}
    </div>
  );
};

export default TypeTaxonomyNav;
